import {
  addLatestPriceOfType,
  assetDataSchema,
  assetMediaSchema,
  assetRelationDataSchema,
  assignAssetTraits,
  getAssetById,
  sourceDataSchema,
  updateAssetRelation,
  updateFinalPrice,
  type Enums,
  type Json,
} from '@aikoa/common'
import { z } from 'zod/v4'
import type { UpdateAssetPayload } from '~/data/assets/types.ts'
import { supabase } from '~/lib/supabase'

export async function deleteAssetPrice(priceId: string) {
  const { error } = await supabase
    .from('asset_prices')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', priceId)

  if (error) {
    console.error(error)
    return null
  }

  return { id: priceId }
}

export async function convertAssetRelation({
  userId,
  assetId,
  fromRelation,
  toRelation,
  data,
}: {
  userId: string
  assetId: string
  fromRelation: Enums<'user_asset_relation'>
  toRelation: Enums<'user_asset_relation'>
  data?: z.infer<typeof assetRelationDataSchema>
}) {
  const validatedData = data ? assetRelationDataSchema.parse(data) : undefined

  const { data: updatedRelation, error } = await supabase
    .from('users_assets')
    .update({
      relation_type: toRelation,
      data: validatedData || undefined,
    })
    .eq('user_id', userId)
    .eq('asset_id', assetId)
    .eq('relation_type', fromRelation)
    .select()
    .maybeSingle()

  if (error || !updatedRelation) {
    console.error(error)
    return null
  }

  return updatedRelation
}

export async function updateAsset({
  assetId,
  assetPayload,
}: {
  assetId: string
  assetPayload: UpdateAssetPayload
}) {
  const user = await supabase.auth.getUser()
  const userId = user.data.user?.id

  if (!userId) {
    console.error('User not found')
    return null
  }

  const asset = await getAssetById(assetId, { supabase })

  if (!asset) {
    console.error('Asset not found')
    return null
  }

  if (!asset.relations.some((relation) => relation.user_id === userId)) {
    console.error('User is not related to the asset')
    return null
  }

  const updatePromises: Promise<unknown>[] = []

  if (assetPayload.traits) {
    updatePromises.push(
      assignAssetTraits(
        {
          assetId,
          traits: assetPayload.traits,
        },
        { supabase }
      )
    )
  }

  const nextAssetData = assetPayload.data
    ? assetDataSchema.parse({
        ...asset.data,
        ...assetPayload.data,
      })
    : asset.data

  const nextAssetSource = assetPayload.source
    ? sourceDataSchema.parse({
        ...asset.source,
        ...assetPayload.source,
      })
    : asset.source

  const currentMediaPaths = asset.media.map((m) => m.storagePath)

  // Replace the media with the new media. Does not merge.
  const nextAssetMedia = assetPayload.media
    ? z.array(assetMediaSchema).parse(assetPayload.media)
    : asset.media

  const deletedMediaPaths = currentMediaPaths.filter(
    (path): path is string => !!path && !nextAssetMedia.some((m) => m.storagePath === path)
  )

  const updatePromise = async () =>
    supabase
      .from('assets')
      .update({
        data: nextAssetData,
        media: nextAssetMedia,
        source: nextAssetSource as Json,
      })
      .eq('id', assetId)
      .then(async () => {
        // Only delete if asset update was successful.
        if (deletedMediaPaths.length > 0) {
          await deleteAssetMedia(deletedMediaPaths)
        }
      })

  updatePromises.push(updatePromise())

  if (assetPayload.prices?.displayPrice) {
    const userRelation = asset.relations.find((relation) => relation.user_id === userId)
    const priceType =
      userRelation?.relation_type === 'prospect' ? 'estimated_price' : 'asking_price'

    updatePromises.push(
      addLatestPriceOfType(
        {
          assetId,
          currentPrices: asset.prices ?? [],
          proposedPrice: assetPayload.prices.displayPrice,
          priceType,
        },
        { supabase }
      )
    )
  }

  if (assetPayload.prices?.finalPrice !== undefined) {
    updatePromises.push(
      updateFinalPrice(assetId, assetPayload.prices.finalPrice, asset.prices ?? [], {
        supabase,
      })
    )
  }

  if (assetPayload.relationData) {
    const userRelation = asset.relations.find((r) => r.user_id === userId)

    if (!userRelation) {
      console.error('User relation not found')
      return null
    }

    updatePromises.push(
      updateAssetRelation(
        {
          assetId,
          userId,
          relationType: userRelation.relation_type,
          data: assetPayload.relationData || userRelation.data || undefined,
        },
        { supabase }
      )
    )
  }

  await Promise.all(updatePromises)
  return getAssetById(assetId, { supabase })
}

export async function deleteAssetMedia(mediaPaths: string[]) {
  const { error } = await supabase.storage.from('property-media').remove(mediaPaths)

  if (error) {
    console.error('Error deleting media:', error)
    return false
  }

  return true
}
